import { useCallback, useEffect, useRef, useState } from 'react';
import { useMooConfig } from '../provider';
import { useShellSelection } from '../provider/hooks';
import type { UploadedFile } from '../types/transport';
import { toCanonicalUuid } from '../utils/canonical-uuid';

export type UploadStatus = 'uploading' | 'done' | 'error';

export interface UploadEntry {
  /** Stable id for React keying + remove gestures. Generated client-side
   *  on add — distinct from `uploaded.id` (server-assigned, set later). */
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly size: number;
  readonly status: UploadStatus;
  /** Populated when the upload completes successfully. */
  readonly uploaded: UploadedFile | null;
  /** Populated when status === 'error'. */
  readonly error: string | null;
}

export interface UseUploadsApi {
  readonly uploads: UploadEntry[];
  readonly addFiles: (files: File[]) => void;
  readonly removeUpload: (id: string) => void;
  readonly clear: () => void;
  /** Drops the given entries once a send has taken them, leaving the rest
   *  (still uploading, or failed) in the composer. Unlike `clear`, keeps
   *  `pendingConversationId` so a follow-up sent before the conversation
   *  binds lands in the same backend thread. */
  readonly discard: (ids: readonly string[]) => void;
  /** Conversation id minted by the upload endpoint for a fresh chat (no
   *  conversation selected yet), remembered so the next send attributes to
   *  the same backend thread the file is already tied to. Not the same as
   *  `selection.conversationId` — setting that would select the conversation
   *  and remount this pane (see `markDone`). */
  readonly pendingConversationId: string | null;
}

interface InFlight {
  readonly controller: AbortController;
}

let idCounter = 0;

function makeId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  idCounter += 1;
  return `upload-${Date.now()}-${idCounter}`;
}

function entryFromFile(file: File, id: string): UploadEntry {
  return {
    id,
    name: file.name,
    mimeType: file.type,
    size: file.size,
    status: 'uploading',
    uploaded: null,
    error: null,
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  // Avoid the default `[object Object]` stringification — JSON-serialize plain
  // objects so the logged message actually carries the error payload.
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

/** Per-pane upload state. Owns the in-flight list, calls
 *  `transport.uploadFile` for each File the user picks, and aborts any
 *  uploads still in flight when the component unmounts. The `paneId` is
 *  forwarded to analytics callers so multi-pane hosts can attribute the
 *  upload to the right pane — the hook itself is single-instance per
 *  call site. */
export function useUploads(paneId: string): UseUploadsApi {
  const { transport, logger, toast } = useMooConfig();
  const { conversationId } = useShellSelection();
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const [pendingConversationId, setPendingConversationId] = useState<
    string | null
  >(null);
  const inflightRef = useRef<Record<string, InFlight>>({});
  // The conversation every upload of a chat not yet started is filed under. Minted on the first upload and sent with
  // each one after, so files added together, or one after another before the first message, land in one conversation:
  // with no id, the upload endpoint mints a new one per file, and the agent only sees the files of its own conversation.
  const provisionalIdRef = useRef<string | null>(null);

  // One AbortController shared across the hook's lifetime. Stable across
  // renders via lazy `useState` init, aborted on unmount. Each per-upload
  // controller subscribes to this signal so a single `abort()` here
  // cascades to every in-flight `transport.uploadFile` call — without
  // needing to read a stale `inflight` snapshot from a closure.
  const [lifetimeController] = useState(() => new AbortController());

  useEffect(() => {
    return () => {
      lifetimeController.abort();
    };
  }, [lifetimeController]);

  const removeUpload = useCallback((id: string) => {
    const entry = inflightRef.current[id];
    if (entry) entry.controller.abort();
    const next = { ...inflightRef.current };
    delete next[id];
    inflightRef.current = next;
    setUploads((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const clear = useCallback(() => {
    Object.values(inflightRef.current).forEach(({ controller }) =>
      controller.abort()
    );
    inflightRef.current = {};
    provisionalIdRef.current = null;
    setUploads([]);
    setPendingConversationId(null);
  }, []);

  const discard = useCallback((ids: readonly string[]) => {
    if (ids.length === 0) return;
    const gone = new Set(ids);
    const next = { ...inflightRef.current };
    ids.forEach((id) => delete next[id]);
    inflightRef.current = next;
    setUploads((prev) => prev.filter((u) => !gone.has(u.id)));
  }, []);

  const settleInflight = useCallback((id: string) => {
    if (!(id in inflightRef.current)) return;
    const next = { ...inflightRef.current };
    delete next[id];
    inflightRef.current = next;
  }, []);

  const markDone = useCallback(
    (id: string, uploaded: UploadedFile) => {
      setUploads((prev) =>
        prev.map((u) => (u.id === id ? { ...u, status: 'done', uploaded } : u))
      );
      if (
        uploaded.conversationId &&
        conversationId === null &&
        pendingConversationId === null
      ) {
        // The upload endpoint mints this conversationId server-side to
        // attribute the file, but no conversation record exists yet — it's
        // only materialized once the first message is sent. Remember it
        // locally (V1 parity) so the next send attributes to the same
        // thread; do NOT call selectConversation here — MooChat keys
        // ChatPanel by conversationId, so selecting this provisional id
        // would remount this whole pane (including this hook) and wipe the
        // very upload state it just finished setting, dropping the file
        // from the send silently (OMNI-584).
        //
        // Canonicalize first — the upload endpoint hands this id back in the
        // same stripped-dash form the chat stream uses, and every downstream
        // consumer (the eventual selection, REST calls) needs to agree with
        // the REST catalog's dashed form or this conversation fragments into
        // two identities (OMNI-621).
        setPendingConversationId(toCanonicalUuid(uploaded.conversationId));
      }
      settleInflight(id);
    },
    [conversationId, pendingConversationId, settleInflight]
  );

  const markError = useCallback(
    (id: string, err: unknown, fileName: string) => {
      setUploads((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, status: 'error', error: errorMessage(err) } : u
        )
      );
      // V1 parity + slice 2c spec: surface upload failures to the user via
      // the host's toast adapter. When the host hasn't wired toast, the
      // chip's error label is the only signal — that's acceptable degrade.
      toast?.error(`Couldn't upload ${fileName}`);
      settleInflight(id);
    },
    [settleInflight, toast]
  );

  /** The conversation an upload is filed under: the selected one, else the one this chat's uploads already share. */
  const uploadConversationId = useCallback((): string => {
    if (conversationId) return conversationId;
    if (pendingConversationId) return pendingConversationId;
    provisionalIdRef.current ??= crypto.randomUUID();
    return provisionalIdRef.current;
  }, [conversationId, pendingConversationId]);

  const startUpload = useCallback(
    (entry: UploadEntry, file: File, controller: AbortController) => {
      transport
        .uploadFile(file, {
          signal: controller.signal,
          conversationId: uploadConversationId(),
        })
        .then((uploaded) => {
          if (controller.signal.aborted) return;
          markDone(entry.id, uploaded);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          logger.warn('moo.upload.error', {
            paneId,
            fileName: entry.name,
            error: errorMessage(err),
          });
          markError(entry.id, err, entry.name);
        });
    },
    [transport, logger, paneId, uploadConversationId, markDone, markError]
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const additions: UploadEntry[] = [];
      const trackers: Record<string, InFlight> = {};
      const starts: Array<{
        entry: UploadEntry;
        file: File;
        controller: AbortController;
      }> = [];

      for (const file of files) {
        const id = makeId();
        const entry = entryFromFile(file, id);
        const controller = new AbortController();
        // Cascade unmount → per-upload abort. If the hook already
        // unmounted (rare; addFiles called from a stale callback), abort
        // immediately so the upload never starts.
        if (lifetimeController.signal.aborted) {
          controller.abort();
        } else {
          lifetimeController.signal.addEventListener(
            'abort',
            () => controller.abort(),
            { once: true }
          );
        }
        additions.push(entry);
        trackers[id] = { controller };
        starts.push({ entry, file, controller });
      }
      setUploads((prev) => [...prev, ...additions]);
      inflightRef.current = { ...inflightRef.current, ...trackers };
      for (const { entry, file, controller } of starts) {
        startUpload(entry, file, controller);
      }
    },
    [lifetimeController, startUpload]
  );

  return {
    uploads,
    addFiles,
    removeUpload,
    clear,
    discard,
    pendingConversationId,
  };
}
